/**
 * `.zwdoc` helpers for change history (#252): plain prose for diffing,
 * the review document, and restoring old versions. Either side of a history entry may be legacy Markdown
 * (before conversion) or `.zwdoc`; both read as the same plain text, so the
 * conversion commit itself shows no wording changes.
 */

import { nothing } from "../maybe";
import { ok, type Result } from "../result";
import {
  emptyZwDocHeader,
  isZwDocText,
  parseZwDoc,
  readZwDocHeader,
  serializeZwDoc,
  type ZwDoc,
  type ZwDocParseError,
} from "./format";
import { markdownToZwDoc, stripLegacyFrontmatter } from "./fromMarkdown";
import { projectPlainText } from "./plainText";
import { zwdocSchema } from "./schema";

/** Any document text as a `ZwDoc`: parsed `.zwdoc`, or converted Markdown. */
export function anyToZwDoc(raw: string): Result<ZwDoc, ZwDocParseError> {
  if (isZwDocText(raw)) return parseZwDoc(raw);
  return markdownToZwDoc({
    markdown: stripLegacyFrontmatter(raw),
    synopsis: "",
    kind: nothing,
    fields: nothing,
  });
}

/**
 * Plain prose for diffing: the projection, with blank paragraphs dropped
 * (`\n{3,}` → `\n\n`) and no leading / trailing breaks. Unreadable text
 * diffs as empty.
 */
export function plainProseForHistory(raw: string): string {
  if (raw.trim() === "") return "";
  const parsed = anyToZwDoc(raw);
  if (parsed.ok === false) return "";
  return projectPlainText(parsed.value.doc)
    .text.replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+/, "")
    .replace(/\n+$/, "");
}

/**
 * `.zwdoc` text whose plain-text projection is exactly `text`: paragraphs
 * split on `\n\n`, single `\n` as hard breaks. Offsets into `text` (history
 * ranges) therefore map 1:1 onto the document.
 */
export function plainTextToZwDoc(text: string): string {
  const s = zwdocSchema;
  const blocks = text.split("\n\n").map((paragraph) => {
    const inline = paragraph
      .split("\n")
      .flatMap((line, i) => [
        ...(i > 0 ? [s.node("hard_break")] : []),
        ...(line === "" ? [] : [s.text(line)]),
      ]);
    return s.node("paragraph", null, inline);
  });
  return serializeZwDoc({
    header: emptyZwDocHeader(),
    doc: s.node("doc", null, blocks),
  });
}

/** `.zwdoc` text for previewing any file (trash): `.zwdoc` as-is, Markdown converted. */
export function previewAsZwDoc(raw: string): Result<string, ZwDocParseError> {
  if (isZwDocText(raw)) return ok(raw);
  const converted = anyToZwDoc(raw);
  return converted.ok ? ok(serializeZwDoc(converted.value)) : converted;
}

/**
 * Text to restore when the live document is `.zwdoc` and the chosen version
 * may predate conversion. A Markdown version is converted; the live header's
 * synopsis, kind and fields are kept (the version's frontmatter is not), and
 * its comments come along converted. Unreadable versions restore as-is.
 */
export function restoreIntoZwDoc(live: string, version: string): string {
  if (isZwDocText(version)) return version;
  const converted = anyToZwDoc(version);
  const liveHeader = readZwDocHeader(live);
  if (converted.ok === false) return version;
  if (liveHeader.ok === false) return serializeZwDoc(converted.value);
  return serializeZwDoc({
    header: { ...liveHeader.value, comments: converted.value.header.comments },
    doc: converted.value.doc,
  });
}
